import { useCallback } from 'react'

import { boardsHolding } from '../../api/boards'
import { useLoad } from '../../api/useLoad'
import { hrefFor } from '../../frame/route'
import { LinkButton } from '../../ui/Control'
import { pathToElement } from '../desk/place'

/**
 * Which boards a document lies on, said at the document.
 *
 * The way back up from the library to the desk. It matters because a card is a *reference*: a
 * document knows nothing about who has laid one on what, so without this line somebody looking at a
 * script has no way of finding the board where they worked out what it belonged with.
 *
 * Asked of the boards rather than answered by the library — a board knows what it holds — and each
 * entry leads to the **place on the board** rather than merely to the board, which is the same
 * address a search hit and the board's own find both write.
 *
 * Silent when there are none, and silent when the question fails. It is an extra way through, and a
 * line saying "this lies on no boards" is a fact about the database that nobody came here to read.
 */
export interface LiesOnProps {
  /** `document`, `note` or `task` — the same polymorphic address the search index uses. */
  readonly kind: string
  readonly id: string
}

export function LiesOn({ kind, id }: LiesOnProps) {
  const holding = useLoad(
    useCallback((signal: AbortSignal) => boardsHolding(kind, id, signal), [kind, id]),
  )

  if (holding.load.status !== 'ready' || holding.load.value.length === 0) {
    return null
  }

  return (
    <p className="lies-on">
      <span className="lies-on__label">Lies on</span>
      {holding.load.value.map((on) => (
        <LinkButton
          key={`${on.board.id}-${on.element}`}
          tone="bare"
          href={hrefFor('desk', pathToElement(on.board.id, on.element))}
        >
          {on.board.name}
        </LinkButton>
      ))}
    </p>
  )
}
